import {useEffect, useState} from "react";
import comparison_api from "../api/protected/comparison";
import back_arrow from "../assets/backarrow.svg";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import ComparisonSurveyCard from "../components/ComparisonSurveyCard";
import ComparisonSummaryChart from "../components/ComparisonSummaryChart";
import { captureElementAsCanvas, canvasToA4PdfDataUri, waitForLayout } from "../utils/captureForPdf";
import DiagnosticEngagementRoleFilter from "../components/DiagnosticEngagementRoleFilter";
import DiagnosticEngagementTabular from "../components/DiagnosticEngagementTabular";
import ComparsionReportConfigurator from "../components/ComparisonReportConfigurator";
import pdf_download from "../assets/download-pdf-temp.png";
import reports_api from "../api/protected/reports";
import CoachCompCommentaryContainer from "../components/CoachCompCommentaryContainer";
import profile_api from "../api/protected/profile";
import SubscriptionNeededPopup from "../components/SubscriptionNeededPopup";
import GenericDialog from "../components/GenericDialog";

const DiagnosticEngagement = () => {
    const navigate = useNavigate();
    const params = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const demoSurveys = searchParams.get('demo')

    const [firstSurveyId, setFirstSurveyId] = useState()
    const [secondSurveyId, setSecondSurveyId] = useState()

    const [data, setData] = useState()
    const [selectedPillar, setSelectedPillar] = useState({id: 0, pillar: "Purpose & Motivation"})
    const [tabularColour, setTabularColour] = useState("blue")
    const [firstThemes, setFirstThemes] = useState()
    const [secondThemes, setSecondThemes] = useState()

    const [showTeamMembers, setShowTeamMembers] = useState(true)
    const [showTeamLeaders, setShowTeamLeaders] = useState(true)
    const [showStakeholders, setShowStakeholders] = useState(true)

    const [showReportGenerated, setShowReportGenerated] = useState(false);
    const [generatingReport, setGeneratingReport] = useState(false)
    const [spacerAdjust, setSpacerAdjust] = useState(0)
    const [showReportCover, setShowReportCover] = useState(false)
    const [showConfigurator, setShowConfigurator] = useState(false)
    const [reportConfig, setReportConfig] = useState(null)

    const [showDetailCards, setShowDetailCards] = useState(true);
    const [showGraphicalCharts, setShowGraphicalCharts] = useState(true);
    const [showDataTables, setShowDataTables] = useState(true);
    const [showReportError, setShowReportError] = useState(false)

    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [hoverSource, setHoverSource] = useState(null);

    const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [showInsufficientOverlap, setShowInsufficientOverlap] = useState(false);

    const handleBackClick = (ev) => {
        if(!ev) return;
        if (window.history.length > -1) {
            navigate(-1)
        } else {
            navigate(`/surveys`)
        }
    }

    const normal_round = (value) => {
        if (!value) return;
        if (value - Math.floor(value) < 0.5) return Math.floor(value);
        return Math.ceil(value)
    }

    useEffect(() => {
        let spacer_adjust = 0;
        if(!showTeamMembers) spacer_adjust += 60;
        if(!showTeamLeaders) spacer_adjust += 60;
        if(!showStakeholders) spacer_adjust += 60;
        setSpacerAdjust(spacer_adjust)
    }, [showTeamMembers, showTeamLeaders, showStakeholders])

    useEffect(() => {
        if (!firstSurveyId || !secondSurveyId) return;
        if (demoSurveys === "true") {
            let response = comparison_api.getDemoComparisons(firstSurveyId, secondSurveyId)
            response.then(response => {
                setData(response.data)
                console.log(response.data)
            }).catch((err) => {
                if (comparison_api.isInsufficientCommonParticipants(err)) {
                    setShowInsufficientOverlap(true)
                } else {
                    console.log("error")
                }
            })
        } else {
            let response = comparison_api.getComparisons(firstSurveyId, secondSurveyId)
            response.then(response => {
                setData(response.data)
            }).catch((err) => {
                if (comparison_api.isInsufficientCommonParticipants(err)) {
                    setShowInsufficientOverlap(true)
                } else {
                    console.log("error")
                }
            })
        }
    }, [firstSurveyId, secondSurveyId]);

    useEffect(() => {
        if (!params.firstSurveyId) return;
        setFirstSurveyId(params.firstSurveyId)
    }, [params.firstSurveyId]);

    useEffect(() => {
        if (!params.secondSurveyId) return;
        setSecondSurveyId(params.secondSurveyId)
    }, [params.secondSurveyId]);

    const getRoleAverageScore = (theme, role) => {
        if (!theme || !role) return 0;
        let _total = 0
        let _length = 0
        switch (role) {
            case "tm":
                for (let i = 0;i<theme.team_member_scores.length;i++) {
                    _total += theme.team_member_scores[i].score
                    _length += 1
                }
                break;
            case "tl":
                for (let i = 0;i<theme.team_leader_scores.length;i++) {
                    _total += theme.team_leader_scores[i].score
                    _length += 1
                }
                break;
            case "sh":
                for (let i = 0;i<theme.stakeholder_scores.length;i++) {
                    _total += theme.stakeholder_scores[i].score
                    _length += 1
                }
                break;
        }
        if (!_length) return 0;
        return normal_round(_total/_length);
    }

    const getTeamAverageScore = (theme) => {
        let _total = 0
        let _length = 0
        if (showTeamMembers) {
            for (let i = 0;i<theme.team_member_scores.length;i++) {
                _total += theme.team_member_scores[i].score
                _length += 1
            }
        }
        if (showTeamLeaders) {
            for (let i = 0;i<theme.team_leader_scores.length;i++) {
                _total += theme.team_leader_scores[i].score
                _length += 1
            }
        }
        if (showStakeholders) {
            for (let i = 0;i<theme.stakeholder_scores.length;i++) {
                _total += theme.stakeholder_scores[i].score
                _length += 1
            }
        }
        if (!_length) return 0;
        return normal_round(_total/_length)
    }

    const getFunctionValue = (themes) => {
        let _score = getTeamAverageScore(themes)

        if (_score >= 40) {
            return "High"
        } else if (_score >= 20 && _score < 40) {
            return "Medium"
        } else {
            return "Low"
        }
    }

    const getSurveyPoint = (point) => {
        let _value = "Initial"
        if (!point) return _value;
        switch (point) {
            case 2: _value = "Midpoint"; break;
            case 3: _value = "Endpoint"; break;
            case 4: _value = "Post Engagement"; break;
        }
        return _value;
    }

    const getFixedTableData = (survey_idx, pillar) => {
        if(!data) return;
        return data.surveys[survey_idx].pillars[pillar].themes
    }

    useEffect(() => {
        if (!selectedPillar || !data) return;
        if (!data.surveys?.[0]?.pillars?.[selectedPillar.id] || !data.surveys?.[1]?.pillars?.[selectedPillar.id]) return;
        let _first_survey = data.surveys[0].pillars[selectedPillar.id].themes
        let _second_survey = data.surveys[1].pillars[selectedPillar.id].themes
        setFirstThemes(_first_survey)
        setSecondThemes(_second_survey)
        switch (selectedPillar.id) {
            case 0: setTabularColour("blue"); break;
            case 1: setTabularColour("red"); break;
            case 2: setTabularColour("green"); break;
            case 3: setTabularColour("purple"); break;
            case 4: setTabularColour("pink"); break;
            case 5: setTabularColour("orange"); break;
        }
    }, [selectedPillar, data]);

    const OpenConfigurator = (ev) => {
        if(!ev) return;
        setShowConfigurator(true)
    }

    const CloseConfigurator = (ev) => {
        if(!ev) return;
        setShowConfigurator(false)
    }

    const HandleConfiguratorGenerate = () => {
        setShowConfigurator(false)
        setShowReportCover(true)
    }

    useEffect(() => {
        if(!showReportCover) return;
        setGeneratingReport(true)
    }, [showReportCover])

    const finaliseReport = (report) => {
        if(!data) return;

        let d1 = new Date(data.surveys[0].survey_created_date * 1000).toLocaleDateString("en-UK")
        let p1 = getSurveyPoint(data.surveys[0].survey_point)
        let d2 = new Date(data.surveys[1].survey_created_date * 1000).toLocaleDateString("en-UK")
        let p2 = getSurveyPoint(data.surveys[1].survey_point)
        let comparing_surveys = `${p1} ${d1} vs ${p2} ${d2}`

        let report_details = {
            "client_name": data.organisation_name,
            "team_name": data.team_name,
            "comparing_surveys": comparing_surveys,
            "tm_count": data.surveys[0].pillars[0].all_question_scores[0].scores.team_member_scores.length,
            "tl_count": data.surveys[0].pillars[0].all_question_scores[0].scores.team_leader_scores.length,
            "st_count": data.surveys[0].pillars[0].all_question_scores[0].scores.stakeholder_scores.length,
            "survey_id": null,
            "team_id": data.surveys[0].team_id
        }
        let response = reports_api.finaliseReport('comparison', report_details, {}, reportConfig, report);
        response.then(() => {
            setShowReportCover(false)
            setGeneratingReport(false)
            setShowReportGenerated(true)
        }).catch(err => {
            console.log(err)
            setShowReportCover(false)
            setGeneratingReport(false)
            setShowReportError(true)
        })

    }

    useEffect(() => {
        if (!generatingReport) return;

        let cancelled = false;

        (async () => {
            await waitForLayout();

            const elem = document.getElementById("de-pdf");
            if (!elem || cancelled) return;

            try {
                const canvas = await captureElementAsCanvas(elem);
                if (cancelled) return;
                finaliseReport({ 0: canvasToA4PdfDataUri(canvas) });
                setGeneratingReport(false);
            } catch {
                setGeneratingReport(false);
                setShowReportCover(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [generatingReport])

    const handlePdfClick = (ev) => {
        if (!subscription || subscription < 1) return setShowSubscriptionPopup(true);
        OpenConfigurator(ev);
    };

    const getSubscription = () => {
        let response = profile_api.getSubscription();
        response.then(response => {
            setSubscription(response.data.subscription.type);
        }).catch(() => {});
    };

    useEffect(() => {
        getSubscription();
    }, []);

    return(
        <>
            <div className={`comparison-page-background-container${generatingReport ? " pdf-capture-active" : ""}`} style={{backgroundColor: generatingReport ? "unset" : null}}>
                <div className={`comparison-page-container${generatingReport ? " pdf-capture-container" : ""}`} id="de-pdf"
                     style={generatingReport ? {padding: "10px"} : null}>
                    <div className={generatingReport ? "hidden" : "link"} onClick={handleBackClick}>
                        <img className={"back-arrow-classname"} src={back_arrow} alt="Back Arrow"/>
                    </div>
                    <div className="comparison-title" style={{margin: generatingReport ? "0 30px" : null}}>
                        Diagnostic Engagement Review
                        <div className="pdf-download-button-container" onClick={handlePdfClick} style={{display: generatingReport ? "none" : null}}>
                            <img src={pdf_download}/>
                        </div>
                    </div>
                    <div className="comparison-header-container">
                        <div className="comparison-header-title-container start">
                        <div className="comparison-header-title">Organisation:</div>
                            <div className="comparison-header-value">{data && data.organisation_name}</div>
                        </div>
                        <div className="comparison-header-title-container end">
                            <div className="comparison-header-title">Team:</div>
                            <div className="comparison-header-value">{data && data.team_name}</div>
                        </div>
                    </div>
                    <div className="comparison-cards-container"
                         style={{margin: generatingReport ? "0 30px" : null, display: !showDetailCards && generatingReport ? "none" : null}}>
                        {data && data.surveys && data.surveys.map((survey, idx) => {
                            return <ComparisonSurveyCard survey={survey} idx={idx} generatingReport={generatingReport}/>
                        })}
                    </div>
                    <div className="pdf-spacer" style={{height: 50, display: generatingReport ? "block" : null}}/>
                    <div className={generatingReport ? "comparison-role-filter-container hidden" : "comparison-role-filter-container"}>
                        <DiagnosticEngagementRoleFilter setValue={setShowTeamMembers} value={showTeamMembers}
                                                        color="green" label="Team Members"/>
                        <DiagnosticEngagementRoleFilter setValue={setShowTeamLeaders} value={showTeamLeaders}
                                                        color="pink" label="Team Leader(s)"/>
                        <DiagnosticEngagementRoleFilter setValue={setShowStakeholders} value={showStakeholders}
                                                        color="blue" label="Stakeholder(s)"/>
                    </div>
                    <div className={`premium-watermark-container ${subscription < 1 && "locked"}`}>
                        <div className="comparison-charts-container" style={{display: !showGraphicalCharts && generatingReport ? "none" : null}}>
                            <div className="comparison-chart-card-container"
                                 style={{height: generatingReport ? "unset" : null}}>
                                <div className="comparison-interactive-chart start">
                                    <ComparisonSummaryChart surveyData={data && data.surveys[0]} tmEnabled={showTeamMembers}
                                                            tlEnabled={showTeamLeaders} stEnabled={showStakeholders}
                                                            showPillars={true} chartId={1} hoveredSegment={hoveredSegment}
                                                            setHoveredSegment={setHoveredSegment} hoverSource={hoverSource}
                                                            setHoverSource={setHoverSource}
                                    />
                                </div>
                            </div>
                            <div className="comparison-chart-card-container"
                                 style={{height: generatingReport ? "unset" : null}}>
                                <div className="comparison-interactive-chart end">
                                    <ComparisonSummaryChart surveyData={data && data.surveys[1]} tmEnabled={showTeamMembers}
                                                            tlEnabled={showTeamLeaders} stEnabled={showStakeholders}
                                                            showPillars={true} chartId={2} hoveredSegment={hoveredSegment}
                                                            setHoveredSegment={setHoveredSegment} hoverSource={hoverSource}
                                                            setHoverSource={setHoverSource}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="pdf-spacer" style={{height: 50, display: generatingReport ? "block" : null}}/>
                    <div className={generatingReport ? "hidden" : "comparison-pillar-filter"}>
                        <div className="comparison-pillar-text">Select Pillar</div>
                        <div className={selectedPillar.id === 0 ?
                            "comparison-pillar-container blue on" : "comparison-pillar-container blue off"}
                             onClick={() => setSelectedPillar({id: 0, pillar: "Purpose & Motivation"})}/>
                        <div className={selectedPillar.id === 1 ?
                            "comparison-pillar-container red on" : "comparison-pillar-container red off"}
                             onClick={() => setSelectedPillar({id: 1, pillar: "External Systems & Processes"})}/>
                        <div className={selectedPillar.id === 2 ?
                            "comparison-pillar-container green on" : "comparison-pillar-container green off"}
                             onClick={() => setSelectedPillar({id: 2, pillar: "Relationships"})}/>
                        <div className={selectedPillar.id === 3 ?
                            "comparison-pillar-container purple on" : "comparison-pillar-container purple off"}
                             onClick={() => setSelectedPillar({id: 3, pillar: "Internal Systems & Processes"})}/>
                        <div className={selectedPillar.id === 4 ?
                            "comparison-pillar-container pink on" : "comparison-pillar-container pink off"}
                             onClick={() => setSelectedPillar({id: 4, pillar: "Learning"})}/>
                        <div className={selectedPillar.id === 5 ?
                            "comparison-pillar-container orange on" : "comparison-pillar-container orange off"}
                             onClick={() => setSelectedPillar({id: 5, pillar: "Leadership"})}/>
                    </div>

                    <CoachCompCommentaryContainer
                        comp_id_first={firstSurveyId}
                        comp_id_second={secondSurveyId}
                        section_id="comparison-overview"
                        generatingReport={generatingReport}
                        subscription={subscription}
                    />


                    <DiagnosticEngagementTabular firstThemes={firstThemes} secondThemes={secondThemes}
                                                 showTeamMembers={showTeamMembers} showTeamLeaders={showTeamLeaders}
                                                 showStakeholders={showStakeholders} tabularColour={tabularColour}
                                                 getSurveyPoint={getSurveyPoint.bind(this)}
                                                 getRoleAverageScore={getRoleAverageScore.bind(this)}
                                                 getTeamAverageScore={getTeamAverageScore.bind(this)}
                                                 getFunctionValue={getFunctionValue.bind(this)}
                                                 data={data} display={!generatingReport} renderDecoration={true}
                                                 title="Tabular"
                                                 subscription={subscription}
                    />

                    <DiagnosticEngagementTabular firstThemes={getFixedTableData(0, 0)}
                                                 secondThemes={getFixedTableData(1, 0)}
                                                 showTeamMembers={showTeamMembers} showTeamLeaders={showTeamLeaders}
                                                 showStakeholders={showStakeholders} tabularColour="blue"
                                                 getSurveyPoint={getSurveyPoint.bind(this)}
                                                 getRoleAverageScore={getRoleAverageScore.bind(this)}
                                                 getTeamAverageScore={getTeamAverageScore.bind(this)}
                                                 getFunctionValue={getFunctionValue.bind(this)}
                                                 data={data} display={generatingReport && showDataTables}
                                                 renderDecoration={false}
                                                 title="Purpose & Motivation"
                                                 subscription={subscription}
                    />
                    <div className="pdf-spacer" style={{height: 170 + spacerAdjust, display: generatingReport ? "block" : null}}/>
                    <DiagnosticEngagementTabular firstThemes={getFixedTableData(0, 1)}
                                                 secondThemes={getFixedTableData(1, 1)}
                                                 showTeamMembers={showTeamMembers} showTeamLeaders={showTeamLeaders}
                                                 showStakeholders={showStakeholders} tabularColour="red"
                                                 getSurveyPoint={getSurveyPoint.bind(this)}
                                                 getRoleAverageScore={getRoleAverageScore.bind(this)}
                                                 getTeamAverageScore={getTeamAverageScore.bind(this)}
                                                 getFunctionValue={getFunctionValue.bind(this)}
                                                 data={data} display={generatingReport && showDataTables}
                                                 renderDecoration={false}
                                                 title="External Systems & Processes"
                                                 subscription={subscription}
                    />
                    <div className="pdf-spacer" style={{height: 50, display: generatingReport ? "block" : null}}/>
                    <DiagnosticEngagementTabular firstThemes={getFixedTableData(0, 2)}
                                                 secondThemes={getFixedTableData(1, 2)}
                                                 showTeamMembers={showTeamMembers} showTeamLeaders={showTeamLeaders}
                                                 showStakeholders={showStakeholders} tabularColour="green"
                                                 getSurveyPoint={getSurveyPoint.bind(this)}
                                                 getRoleAverageScore={getRoleAverageScore.bind(this)}
                                                 getTeamAverageScore={getTeamAverageScore.bind(this)}
                                                 getFunctionValue={getFunctionValue.bind(this)}
                                                 data={data} display={generatingReport && showDataTables}
                                                 renderDecoration={false}
                                                 title="Relationships"
                                                 subscription={subscription}
                    />
                    <div className="pdf-spacer" style={{height: 500 + (2 * spacerAdjust), display: generatingReport ? "block" : null}}/>
                    <DiagnosticEngagementTabular firstThemes={getFixedTableData(0, 3)}
                                                 secondThemes={getFixedTableData(1, 3)}
                                                 showTeamMembers={showTeamMembers} showTeamLeaders={showTeamLeaders}
                                                 showStakeholders={showStakeholders} tabularColour="purple"
                                                 getSurveyPoint={getSurveyPoint.bind(this)}
                                                 getRoleAverageScore={getRoleAverageScore.bind(this)}
                                                 getTeamAverageScore={getTeamAverageScore.bind(this)}
                                                 getFunctionValue={getFunctionValue.bind(this)}
                                                 data={data} display={generatingReport && showDataTables}
                                                 renderDecoration={false}
                                                 title="Internal Systems & Processes"
                                                 subscription={subscription}
                    />
                    <div className="pdf-spacer" style={{height: 50, display: generatingReport ? "block" : null}}/>
                    <DiagnosticEngagementTabular firstThemes={getFixedTableData(0, 4)}
                                                 secondThemes={getFixedTableData(1, 4)}
                                                 showTeamMembers={showTeamMembers} showTeamLeaders={showTeamLeaders}
                                                 showStakeholders={showStakeholders} tabularColour="pink"
                                                 getSurveyPoint={getSurveyPoint.bind(this)}
                                                 getRoleAverageScore={getRoleAverageScore.bind(this)}
                                                 getTeamAverageScore={getTeamAverageScore.bind(this)}
                                                 getFunctionValue={getFunctionValue.bind(this)}
                                                 data={data} display={generatingReport && showDataTables}
                                                 renderDecoration={false}
                                                 title="Learning"
                                                 subscription={subscription}
                    />
                    <div className="pdf-spacer" style={{height: 500 + (2 * spacerAdjust), display: generatingReport ? "block" : null}}/>
                    <DiagnosticEngagementTabular firstThemes={getFixedTableData(0, 5)}
                                                 secondThemes={getFixedTableData(1, 5)}
                                                 showTeamMembers={showTeamMembers} showTeamLeaders={showTeamLeaders}
                                                 showStakeholders={showStakeholders} tabularColour="orange"
                                                 getSurveyPoint={getSurveyPoint.bind(this)}
                                                 getRoleAverageScore={getRoleAverageScore.bind(this)}
                                                 getTeamAverageScore={getTeamAverageScore.bind(this)}
                                                 getFunctionValue={getFunctionValue.bind(this)}
                                                 data={data} display={generatingReport && showDataTables}
                                                 renderDecoration={false}
                                                 title="Leadership"
                                                 subscription={subscription}
                    />

                    <CoachCompCommentaryContainer
                        comp_id_first={firstSurveyId}
                        comp_id_second={secondSurveyId}
                        section_id="comparison-tabular"
                        generatingReport={generatingReport}
                        subscription={subscription}
                    />

                </div>
            </div>
            {showConfigurator ?
                <ComparsionReportConfigurator generate={HandleConfiguratorGenerate.bind(this)}
                                              close={CloseConfigurator.bind(this)}
                                              saveConfig={setReportConfig.bind(this)}
                                              showDetailCards={showDetailCards}
                                              setShowDetailCards={setShowDetailCards.bind(this)}
                                              showGraphicalCharts={showGraphicalCharts}
                                              setShowGraphicalCharts={setShowGraphicalCharts.bind(this)}
                                              showDataTables={showDataTables}
                                              setShowDataTables={setShowDataTables.bind(this)}
                /> : null
            }
            {showReportCover ?
                <div className="report-generation-cover bold">
                    <div className="lds-ring">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                    Please wait, your report is being generated
                </div> : null
            }
            {showReportError ?
                <>
                    <div className="page-mask" onClick={() => setShowReportError(false)}/>
                    <div className="report-error-popup">
                        <div className="report-error-title bold">Something Went Wrong</div>
                        <div className="report-error-description regular">
                            Failed to generate report. Please try again later.
                        </div>
                        <div className="report-error-button bold" onClick={() => setShowReportError(false)}>
                            OK
                        </div>
                    </div>
                </> : null
            }
            {showReportGenerated &&
                <>
                    <div className="page-mask" onClick={() => setShowReportGenerated(false)}/>
                    <div className="report-error-popup">
                        <div className="report-error-title bold">Report Generated</div>
                        <div className="report-error-description regular">
                            Your report has been generated and has been added to the reports table.
                            Click Reports in the menu to see this table.
                        </div>
                        <div className="report-error-button bold" onClick={() => setShowReportGenerated(false)}>
                            OK
                        </div>
                    </div>
                </>
            }
            {showSubscriptionPopup && (
                <SubscriptionNeededPopup
                    onClose={() => setShowSubscriptionPopup(false)}
                />
            )}
            {showInsufficientOverlap && (
                <GenericDialog
                    title="Comparison Summary"
                    text={comparison_api.INSUFFICIENT_COMMON_PARTICIPANTS_MESSAGE}
                    buttons={[{"text": "OK", "id": "1"}]}
                    result={() => handleBackClick({})}
                />
            )}

        </>
    )
}

export default DiagnosticEngagement